import { useAppDispatch, useAppSelector } from '@config/hooks';
import { images, svgs } from '@constants';
import { InfoUserIcon, RoleIcon, InfoStoreIcon } from '@constants/svgs';
import { EUserRoles, TUserRole } from '@models/user';
import { Divider, Dropdown, Image } from 'antd';
import { DropdownWrapper, ModalLogoutWrapper } from './HeaderStyled';
import { authActions } from '@redux/slices/authSlice';
import { useState } from 'react';
import {
  LOGOUT_FORM,
  USER_ROLE_DISPLAY_NAME,
} from '@layouts/LayoutAdmin/constants';

const getRoleDisplay = (roles: TUserRole[] | null | undefined): string => {
  if (!roles || roles.length === 0) {
    return '—';
  }
  if (roles.some((r) => r?.roleCode === EUserRoles.ADMIN)) {
    return USER_ROLE_DISPLAY_NAME[EUserRoles.ADMIN];
  }
  if (roles.some((r) => r?.roleCode === EUserRoles.AREA_OWNER)) {
    return USER_ROLE_DISPLAY_NAME[EUserRoles.AREA_OWNER];
  }
  if (roles.some((r) => r?.roleCode === EUserRoles.STORE_OWNER)) {
    return USER_ROLE_DISPLAY_NAME[EUserRoles.STORE_OWNER];
  }
  return '—';
};

function DropdownButton() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [modalLogout, setModalLogout] = useState(false);
  const [loading, setIsLoading] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);

  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const dispatch = useAppDispatch();

  const isAdmin =
    !!currentUser &&
    currentUser.role.some((r) => r?.roleCode === EUserRoles.ADMIN);
  // AREA_OWNER (地区長) has no single assigned store, so the store row is hidden too.
  const isAreaOwner =
    !!currentUser &&
    currentUser.role.some((r) => r?.roleCode === EUserRoles.AREA_OWNER);

  const handleLogout = () => {
    setIsLoading(true);
    setLogoutError(null);
    dispatch(
      authActions.logout({
        data: '',
        onError: () =>
          setLogoutError('ログアウトに失敗しました。再度お試しください。'),
        onFinal: () => setIsLoading(false),
      })
    );
  };

  return (
    currentUser && (
      <>
        <DropdownWrapper>
          <Dropdown
            overlayClassName="ant-dropdown-container"
            open={dropdownOpen}
            onOpenChange={setDropdownOpen}
            dropdownRender={() => (
              <div className="dropdown-custom">
                {/* VAMS_PC_003 v1.1: dropdown/popover panel (Screen Spec — không phải modal).
                    Class "profile-modal_*" giữ song song với "dropdown-custom_*" để không đổi
                    CSS/hiển thị hiện có (index.css) đồng thời khớp quy ước class đã dùng trong
                    Page Object (e2e/pages/ProfilePage.ts) — UTC-TC004 (PC009) / TC-PC003-xxx. */}
                <div className="dropdown-custom_top profile-modal_top">
                  <span className="dropdown-custom_top-title profile-modal_top-title">
                    VAMS
                  </span>
                  <span
                    onClick={() => {
                      setDropdownOpen(false);
                      setLogoutError(null);
                      setModalLogout(true);
                    }}
                    className="dropdown-custom_top-btn profile-modal_top-btn"
                  >
                    ログアウト
                  </span>
                </div>
                <Divider type="horizontal" />
                <div className="dropdown-custom_bottom">
                  <div>
                    <Image
                      src={svgs.user}
                      alt="avatar"
                      preview={false}
                      className="avatar-img"
                    />
                  </div>
                  <div className="dropdown-custom_bottom-info">
                    <p className="dropdown-custom_bottom-info-id profile-modal_bottom-info-id">
                      {currentUser.fullName ?? '—'}
                    </p>
                    <p className="dropdown-custom_bottom-info-common profile-modal_bottom-info-common">
                      <InfoUserIcon />
                      <span>{currentUser.username ?? '—'}</span>
                    </p>
                    <p className="dropdown-custom_bottom-info-common profile-modal_bottom-info-common">
                      <RoleIcon />
                      <span>{getRoleDisplay(currentUser.role)}</span>
                    </p>
                    {!isAdmin && !isAreaOwner && (
                      <p className="dropdown-custom_bottom-info-common profile-modal_bottom-info-common">
                        <InfoStoreIcon />
                        <span>{currentUser.storeName ?? '—'}</span>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            trigger={['click']}
          >
            <span onClick={(e) => e.preventDefault()}>
              <Image
                src={svgs.user}
                alt="avatar"
                preview={false}
                className="avatar-img"
              />
              <span className="avatar-name">{currentUser.fullName ?? '—'}</span>
            </span>
          </Dropdown>
        </DropdownWrapper>

        <ModalLogoutWrapper
          centered
          confirmLoading={loading}
          closeIcon={false}
          open={modalLogout}
          title="ログアウト確認"
          onOk={() => {
            handleLogout();
          }}
          onCancel={() => setModalLogout(false)}
          afterClose={() => setLogoutError(null)}
          maskClosable={false}
          okText={LOGOUT_FORM.LOGOUT_MODAL_OK}
          cancelText={LOGOUT_FORM.LOGOUT_MODAL_CANCEL}
          width={416}
        >
          <img
            src={images.logout}
            alt="modal_logout"
            className="logout-warning-icon"
          />
          <p className="logout-message">ログアウトしますか？</p>
          {logoutError && <p className="logout-error">{logoutError}</p>}
        </ModalLogoutWrapper>
      </>
    )
  );
}

export { DropdownButton };
